import { useState, useCallback, useEffect, useMemo } from 'react';
import ReactQuill from 'react-quill';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AInput,
  ASwitch,
  ContainerForm,
  OptionFooter,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../../components';
import { useToast } from '../../../hooks/toast';
import {
  FileOutlined,
  InfoCircleOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { Col, Form, Row, Tag, Tooltip, Space, Button } from 'antd';
import { schemaHealthCertificate } from '../schemas/yup.health-certificate.schema';
import api from '../../../services/api';
import { IModelHealthCertificate } from '../../../shared/interfaces';
import {
  formatsText,
  modulesText,
} from '../../../shared/utils/propreties-text';
import { useI18n } from '../../../shared/i18n';
import * as S from './styles';

const ModelHealthCertificateCreateEdit: React.FC = () => {
  const { id } = useParams();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [title, setTitle] = useState<string>();
  const [text, setText] = useState<string>();
  const [status, setStatus] = useState<boolean>(false);

  const loadData = useCallback(async () => {
    interface IResponseHealthCertificate {
      data: IModelHealthCertificate;
    }

    const response = await api.get(`/health-certificates/${id}`);
    const { data }: IResponseHealthCertificate = response;
    setTitle(data.title);
    setText(data.text_description);
    setStatus(data.status);
  }, []);

  const handleCreateSubmit = useCallback(async () => {
    schemaHealthCertificate
      .validate({
        title,
        text_description: text,
      })
      .then(async (validated) => {
        await api
          .post(`/health-certificates/`, validated)
          .then(() => {
            addToast({
              type: 'success',
              title: t('modelsHealthCertificatesCreateEdit.toast.createdTitle'),
              description: t(
                'modelsHealthCertificatesCreateEdit.toast.createdDescription',
              ),
            });
            setTimeout(function () {
              navigate(-1);
            }, 1100);
          })
          .catch((error) => {
            const message = error.response?.data?.message;
            addToast({
              type: 'error',
              title: t('modelsHealthCertificatesCreateEdit.toast.errorTitle'),
              description:
                (Array.isArray(message) ? message[0] : message) ??
                t('modelsHealthCertificatesCreateEdit.toast.createError'),
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('modelsHealthCertificatesCreateEdit.toast.blankFieldsTitle'),
          description: err.errors[0],
        });
      });
  }, [addToast, navigate, text, title, t]);

  const handleEditSubmit = useCallback(async () => {
    schemaHealthCertificate
      .validate({
        title,
        text_description: text,
        status,
      })
      .then(async (validated) => {
        await api
          .patch(`/health-certificates/${id}`, validated)
          .then(() => {
            addToast({
              type: 'info',
              title: t('modelsHealthCertificatesCreateEdit.toast.updatedTitle'),
              description: t(
                'modelsHealthCertificatesCreateEdit.toast.updatedDescription',
              ),
            });
          })
          .catch((error) => {
            const message = error.response?.data?.message;
            addToast({
              type: 'error',
              title: t('modelsHealthCertificatesCreateEdit.toast.errorTitle'),
              description:
                (Array.isArray(message) ? message[0] : message) ??
                t('modelsHealthCertificatesCreateEdit.toast.editError'),
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('modelsHealthCertificatesCreateEdit.toast.blankFieldsTitle'),
          description: err.errors[0],
        });
      });
  }, [addToast, id, status, text, title, t]);

  useEffect(() => {
    if (id !== 'new') {
      loadData();
    }
  }, [id]);

  const handleCopyKeyword = (keyword: string) => {
    navigator.clipboard.writeText(keyword);
    addToast({
      type: 'success',
      title: t('healthCertificateCreateEdit.toast.keywordCopiedTitle'),
      description: t(
        'healthCertificateCreateEdit.toast.keywordCopiedDescription',
        {
          keyword,
        },
      ),
    });
  };

  const keywords = useMemo(
    () => [
      {
        key: '{nome}',
        description: t('healthCertificateCreateEdit.keywords.patientName'),
      },
      {
        key: '{cpf}',
        description: t('healthCertificateCreateEdit.keywords.patientCpf'),
      },
      {
        key: '{qtdDias}',
        description: t('healthCertificateCreateEdit.keywords.daysCount'),
      },
      {
        key: '{data}',
        description: t('healthCertificateCreateEdit.keywords.appointmentDate'),
      },
      {
        key: '{horaInicial}',
        description: t('healthCertificateCreateEdit.keywords.startTime'),
      },
      {
        key: '{horaFinal}',
        description: t('healthCertificateCreateEdit.keywords.endTime'),
      },
      {
        key: '{cid}',
        description: t('healthCertificateCreateEdit.keywords.cid'),
      },
    ],
    [t],
  );

  return (
    <PageLayout>
      <PageIntro
        badge={t('modelsHealthCertificatesCreateEdit.intro.badge')}
        icon={<FileOutlined />}
        title={
          id === 'new'
            ? t('modelsHealthCertificatesCreateEdit.intro.createTitle')
            : t('modelsHealthCertificatesCreateEdit.intro.editTitle')
        }
        description={t('modelsHealthCertificatesCreateEdit.intro.description')}
      />
      <PagePanel>
        <ContainerForm>
          <Form layout="vertical" style={{ width: '90%', marginTop: 24 }}>
            {/* Status Badge */}
            {id !== 'new' && (
              <S.StatusBadge
                count={
                  <Tag color={status ? 'green' : 'red'}>
                    {status
                      ? t('modelsHealthCertificatesCreateEdit.status.active')
                      : t('modelsHealthCertificatesCreateEdit.status.inactive')}
                  </Tag>
                }
              />
            )}

            <S.StyledCard>
              <S.FormSectionTitle>
                <span className="icon">📝</span>
                {t('modelsHealthCertificatesCreateEdit.sections.basicInfo')}
              </S.FormSectionTitle>
              <S.InstructionBox>
                <InfoCircleOutlined className="icon" />
                <p className="text">
                  {t(
                    'modelsHealthCertificatesCreateEdit.instructions.basicInfo',
                  )}
                </p>
              </S.InstructionBox>
              <Row>
                <Col span={24}>
                  <Form.Item
                    label={t('modelsHealthCertificatesCreateEdit.fields.title')}
                    required
                    tooltip={t(
                      'modelsHealthCertificatesCreateEdit.tooltips.title',
                    )}
                  >
                    <Tooltip
                      title={t(
                        'modelsHealthCertificatesCreateEdit.placeholders.titleHelp',
                      )}
                    >
                      <AInput
                        value={title}
                        placeholder={t(
                          'modelsHealthCertificatesCreateEdit.placeholders.title',
                        )}
                        onChange={(e) => setTitle(e.target.value)}
                        maxLength={100}
                      />
                    </Tooltip>
                  </Form.Item>
                </Col>
              </Row>
            </S.StyledCard>

            <S.StyledCard>
              <S.FormSectionTitle>
                <span className="icon">🔑</span>
                {t('modelsHealthCertificatesCreateEdit.sections.keywords')}
              </S.FormSectionTitle>

              <S.InstructionBox>
                <InfoCircleOutlined className="icon" />
                <p className="text">
                  {t(
                    'modelsHealthCertificatesCreateEdit.instructions.keywords',
                  )}
                </p>
              </S.InstructionBox>

              <Space direction="vertical" style={{ width: '100%' }} size={0}>
                {keywords.map((item, index) => (
                  <S.KeywordItem key={`keyword-${index}`}>
                    <span className="keyword-key">{item.key}</span>
                    <span className="keyword-desc">{item.description}</span>
                    <Tooltip
                      title={t(
                        'healthCertificateCreateEdit.actions.copyKeyword',
                      )}
                    >
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => handleCopyKeyword(item.key)}
                        className="copy-btn"
                      />
                    </Tooltip>
                  </S.KeywordItem>
                ))}
              </Space>

              <div
                style={{
                  marginTop: 16,
                  padding: '12px 12px',
                  backgroundColor: '#fffbe6',
                  borderLeft: '3px solid #faad14',
                  borderRadius: '4px',
                }}
              >
                <strong style={{ color: '#ad6800' }}>
                  {t('healthCertificateCreateEdit.warning.title')}
                </strong>
                <p
                  style={{
                    marginBottom: 0,
                    marginTop: 4,
                    color: '#ad6800',
                    fontSize: 13,
                  }}
                >
                  {t('healthCertificateCreateEdit.warning.descriptionStart')}{' '}
                  <code
                    style={{
                      backgroundColor: '#fff7e6',
                      padding: '2px 4px',
                      borderRadius: '2px',
                    }}
                  >
                    {'{'} {'}'}
                  </code>{' '}
                  {t('healthCertificateCreateEdit.warning.descriptionEnd')}
                </p>
              </div>
            </S.StyledCard>

            <S.StyledCard>
              <S.FormSectionTitle>
                <span className="icon">📄</span>
                {t('modelsHealthCertificatesCreateEdit.sections.content')}
              </S.FormSectionTitle>

              <S.InstructionBox>
                <InfoCircleOutlined className="icon" />
                <p className="text">
                  {t('modelsHealthCertificatesCreateEdit.instructions.content')}
                </p>
              </S.InstructionBox>

              <Row>
                <Col span={24}>
                  <Form.Item
                    label={t('modelsHealthCertificatesCreateEdit.fields.text')}
                    required
                  >
                    <ReactQuill
                      theme="snow"
                      value={text}
                      onChange={setText}
                      modules={modulesText}
                      formats={formatsText}
                      style={{ height: '35vh', marginBottom: 20 }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </S.StyledCard>

            {id !== 'new' && (
              <S.StyledCard>
                <S.FormSectionTitle>
                  <span className="icon">✅</span>
                  {t('modelsHealthCertificatesCreateEdit.sections.status')}
                </S.FormSectionTitle>
                <Row>
                  <Col span={24}>
                    <Form.Item
                      label={t(
                        'modelsHealthCertificatesCreateEdit.fields.status',
                      )}
                      tooltip={t(
                        'modelsHealthCertificatesCreateEdit.tooltips.status',
                      )}
                    >
                      <ASwitch
                        checked={status}
                        checkedChildren={t(
                          'modelsHealthCertificatesCreateEdit.status.active',
                        )}
                        unCheckedChildren={t(
                          'modelsHealthCertificatesCreateEdit.status.inactive',
                        )}
                        onChange={() => {
                          setStatus(!status);
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </S.StyledCard>
            )}

            <OptionFooter
              onClickCancel={() => navigate(-1)}
              onClickOk={id !== 'new' ? handleEditSubmit : handleCreateSubmit}
            />
          </Form>
        </ContainerForm>
      </PagePanel>
    </PageLayout>
  );
};

export default ModelHealthCertificateCreateEdit;
